// frontend/src/pages/adolescentes/AdolescentesFormPage.jsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import adolescenteService from '../../services/adolescenteService';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AutocompleteInput from '../../components/common/AutocompleteInput';
import AppSelect from '../../components/common/AppSelect';
import MovimentacoesGrid from '../../components/adolescentes/MovimentacoesGrid';

const SEXO_OPCOES = ['Masculino', 'Feminino', 'Outros'];
const PANEL_THEME_STORAGE_KEY = 'panelLightMode';
const PANEL_THEME_EVENT = 'panel-theme-changed';
const INITIAL_FORM_DATA = {
  nome_adolescente: '',
  nome_social: '',
  dt_nasc_adolescente: '',
  sexo: '',
  id_identidade_genero: '',
  nome_da_mae: '',
  jornada_id: '',
};

function AdolescentesFormPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // reservado para futura edição

  // ⬇️ Removido 'situacao_id' (campo não existe mais no cadastro de Adolescente)
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const [identidadesGenero, setIdentidadesGenero] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [movsLoading, setMovsLoading] = useState(false);
  const [movsError, setMovsError] = useState(null);
  const [movsList, setMovsList] = useState([]);
  const [movsPagination, setMovsPagination] = useState(null);
  const [movsCurrentPage, setMovsCurrentPage] = useState(1);
  const movsAbortRef = useRef(null);
  const [lightMode, setLightMode] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = sessionStorage.getItem(PANEL_THEME_STORAGE_KEY);
    if (saved === null) return true;
    return saved === 'true';
  });

  const isEditing = !!id;
  const pageTitle = isEditing ? 'Editar Adolescente' : 'Novo Adolescente';
  const pageSubtitle = isEditing
    ? `Editando o registro #${id}`
    : 'Preencha os dados para cadastrar um novo adolescente no sistema';
  const panelTheme = lightMode ? 'light' : 'dark';
  const panelThemePageClassName = `adolescentes-form-page panel-theme panel-theme--${panelTheme}`;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncPanelTheme = (event) => {
      const nextMode = event?.detail?.lightMode;
      if (typeof nextMode === 'boolean') {
        setLightMode(nextMode);
        return;
      }

      const saved = sessionStorage.getItem(PANEL_THEME_STORAGE_KEY);
      setLightMode(saved !== 'false');
    };

    window.addEventListener(PANEL_THEME_EVENT, syncPanelTheme);
    return () => {
      window.removeEventListener(PANEL_THEME_EVENT, syncPanelTheme);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // ⬇️ Carrega somente o domínio necessário
        const idsGenResp = await api.get('/identidades-genero');
        setIdentidadesGenero(idsGenResp.data?.data || []);

        if (isEditing) {
          const resp = await api.get(`/adolescentes/${id}`);
          const data = resp.data?.data || {};
          setFormData({
            nome_adolescente: data.nome_adolescente || '',
            nome_social: data.nome_social || '',
            dt_nasc_adolescente: data.dt_nasc_adolescente || '',
            sexo: data.sexo || '',
            // aceita tanto id direto quanto objeto carregado no resource
            id_identidade_genero: data.id_identidade_genero
              ? (typeof data.id_identidade_genero === 'object' && data.id_identidade_genero.id
                  ? String(data.id_identidade_genero.id)
                  : String(data.id_identidade_genero))
              : (data.identidade_genero && data.identidade_genero.id
                  ? String(data.identidade_genero.id)
                  : ''),
            nome_da_mae: data.nome_da_mae || '',
            jornada_id: data.jornada_id || '',
          });
        }
      } catch (err) {
        setError('Não foi possível carregar os dados de apoio do formulário.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing]);

  useEffect(() => {
    if (!isEditing || !id) return undefined;

    movsAbortRef.current?.abort?.();
    const controller = new AbortController();
    movsAbortRef.current = controller;

    const fetchMovs = async () => {
      setMovsLoading(true);
      setMovsError(null);
      try {
        const resp = await api.get(`/adolescentes/${id}/movimentacoes?page=${movsCurrentPage}`, {
          signal: controller.signal,
        });
        setMovsList(resp?.data?.data || []);
        setMovsPagination(resp?.data?.meta || null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setMovsError(
          err?.response?.status === 403
            ? 'Acesso não permitido.'
            : 'Não foi possível carregar as movimentações.'
        );
        setMovsList([]);
        setMovsPagination(null);
      } finally {
        if (!controller.signal.aborted) setMovsLoading(false);
      }
    };

    fetchMovs();
    return () => controller.abort();
  }, [id, isEditing, movsCurrentPage]);

  useEffect(() => {
    setMovsCurrentPage(1);
  }, [id]);

  const handleMovsPageChange = (newPage) => {
    if (movsPagination && newPage > 0 && newPage <= movsPagination.last_page) {
      setMovsCurrentPage(newPage);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  // Handler para autocomplete de adolescentes
  const fetchAdolescenteSuggestions = async (search) => {
    try {
      return await adolescenteService.autocomplete(search, 10);
    } catch (error) {
      console.error('Erro ao buscar sugestões de adolescentes:', error);
      return [];
    }
  };

  const handleSelectAdolescente = (adolescente) => {
    if (adolescente?.id) {
      setError(null);
      setValidationErrors({});
      setMovsError(null);
      setMovsList([]);
      setMovsPagination(null);
      setFormData(INITIAL_FORM_DATA);
      navigate(`/painel/adolescentes/${adolescente.id}/editar`);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      nome_adolescente: adolescente.nome_adolescente,
    }));
    if (validationErrors.nome_adolescente) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.nome_adolescente;
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setValidationErrors({});

    // ⬇️ Payload sem 'situacao_id'
    const payload = {
      nome_adolescente: formData.nome_adolescente,
      nome_social: formData.nome_social || null,
      dt_nasc_adolescente: formData.dt_nasc_adolescente,
      sexo: formData.sexo,
      id_identidade_genero: formData.id_identidade_genero,
      nome_da_mae: formData.nome_da_mae || null,
      jornada_id: formData.jornada_id || null,
    };

    try {
      if (isEditing) {
        await api.put(`/adolescentes/${id}`, payload);
        navigate('/painel/adolescentes');
      } else {
        const resp = await api.post('/adolescentes', payload);
        const novoId = resp?.data?.data?.id;
        if (novoId) {
          // Fluxo segue para criar Movimentação após criar o Adolescente
          navigate(`/painel/adolescentes/${novoId}/movimentacoes/novo`);
        } else {
          navigate('/painel/adolescentes');
        }
      }
    } catch (err) {
      if (err.response?.status === 422) {
        setError('Por favor, corrija os erros no formulário.');
        setValidationErrors(err.response.data.errors || {});
      } else {
        setError(err.response?.data?.message || 'Ocorreu um erro ao salvar o registro.');
        console.error('Erro ao salvar adolescente:', err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={panelThemePageClassName}>
        <LoadingSpinner text="Carregando dados do formulário..." />
      </div>
    );
  }

  return (
    <div className={panelThemePageClassName}>
      <div className="content-header">
        <h1 className="content-title">{pageTitle}</h1>
        <p className="content-subtitle">{pageSubtitle}</p>
      </div>

      <div className="card adolescentes-form-card">
        <div className="card-body">
          <form className="adolescentes-form" onSubmit={handleSubmit} noValidate>
            {error && <div className="alert alert-danger">{error}</div>}

            {/* Identificação */}
            <fieldset className="adolescentes-form-section">
              <legend className="h5 mb-3">Identificação</legend>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="nome_adolescente" className="form-label">Nome Completo <span className="text-danger">*</span></label>
                  <AutocompleteInput
                    id="nome_adolescente"
                    name="nome_adolescente"
                    value={formData.nome_adolescente}
                    onChange={handleChange}
                    onSelect={handleSelectAdolescente}
                    fetchSuggestions={fetchAdolescenteSuggestions}
                    placeholder="Digite o nome do adolescente..."
                    className={`form-control ${validationErrors.nome_adolescente ? 'is-invalid' : ''}`}
                    required
                    minChars={2}
                    debounceMs={300}
                    noResultsText="Nenhum adolescente encontrado com este nome"
                    loadingText="Buscando adolescentes..."
                  />
                  {validationErrors.nome_adolescente && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.nome_adolescente.join(', ')}
                    </div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="nome_social" className="form-label">Nome Social (opcional)</label>
                  <input
                    type="text"
                    id="nome_social"
                    name="nome_social"
                    className={`form-control ${validationErrors.nome_social ? 'is-invalid' : ''}`}
                    value={formData.nome_social}
                    onChange={handleChange}
                    placeholder="Ex.: João da Silva"
                  />
                  {validationErrors.nome_social && (
                    <div className="invalid-feedback">
                      {validationErrors.nome_social.join(', ')}
                    </div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label htmlFor="dt_nasc_adolescente" className="form-label">Data de Nascimento <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    id="dt_nasc_adolescente"
                    name="dt_nasc_adolescente"
                    className={`form-control ${validationErrors.dt_nasc_adolescente ? 'is-invalid' : ''}`}
                    value={formData.dt_nasc_adolescente}
                    onChange={handleChange}
                    required
                  />
                  {validationErrors.dt_nasc_adolescente && (
                    <div className="invalid-feedback">
                      {validationErrors.dt_nasc_adolescente.join(', ')}
                    </div>
                  )}
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="sexo" className="form-label">Sexo <span className="text-danger">*</span></label>
                  <AppSelect
                    className={`form-app-select ${validationErrors.sexo ? 'is-invalid' : ''}`}
                    ariaLabel="Sexo"
                    options={SEXO_OPCOES.map((opt) => ({ value: opt, label: opt }))}
                    value={formData.sexo}
                    onChange={(value) => handleSelectChange('sexo', value)}
                    placeholder="Selecione..."
                  />
                  {validationErrors.sexo && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.sexo.join(', ')}
                    </div>
                  )}
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="id_identidade_genero" className="form-label">Identidade de Gênero <span className="text-danger">*</span></label>
                  <AppSelect
                    className={`form-app-select ${validationErrors.id_identidade_genero ? 'is-invalid' : ''}`}
                    ariaLabel="Identidade de Gênero"
                    options={identidadesGenero.map((g) => ({ value: String(g.id), label: g.nome }))}
                    value={formData.id_identidade_genero}
                    onChange={(value) => handleSelectChange('id_identidade_genero', value)}
                    placeholder="Selecione..."
                  />
                  {validationErrors.id_identidade_genero && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.id_identidade_genero.join(', ')}
                    </div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-8 mb-3">
                  <label htmlFor="nome_da_mae" className="form-label">Nome da Mãe</label>
                  <input
                    type="text"
                    id="nome_da_mae"
                    name="nome_da_mae"
                    className={`form-control ${validationErrors.nome_da_mae ? 'is-invalid' : ''}`}
                    value={formData.nome_da_mae}
                    onChange={handleChange}
                    placeholder="Ex.: Maria de Souza"
                  />
                  {validationErrors.nome_da_mae && (
                    <div className="invalid-feedback">
                      {validationErrors.nome_da_mae.join(', ')}
                    </div>
                  )}
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="jornada_id" className="form-label">ID Jornada</label>
                  <input
                    type="text"
                    id="jornada_id"
                    name="jornada_id"
                    className={`form-control ${validationErrors.jornada_id ? 'is-invalid' : ''}`}
                    value={formData.jornada_id}
                    onChange={handleChange}
                    placeholder="Ex.: JRN12345"
                  />
                  {validationErrors.jornada_id && (
                    <div className="invalid-feedback">
                      {validationErrors.jornada_id.join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* ⬇️ REMOVIDO: bloco de seleção de 'Situacao' */}
            </fieldset>

            <hr className="adolescentes-form-divider my-4" />

            <div className="adolescentes-form-actions d-flex justify-content-end mt-4">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => navigate('/painel/adolescentes')}
                disabled={saving}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar Adolescente'}
              </button>
            </div>
          </form>
        </div>

        {isEditing && (movsLoading || movsError || movsList.length > 0) && (
          <div className="card-body adolescentes-edit-movs pt-0">
            <hr className="adolescentes-form-divider my-0 mb-4" />
            <h2 className="h5 mb-3 adolescentes-edit-movs-title">Movimentações</h2>
            <div className="movs-modal-lista-movimentacoes">
              <MovimentacoesGrid
                loading={movsLoading}
                error={movsError}
                movimentacoes={movsList}
                pagination={movsPagination}
                currentPage={movsCurrentPage}
                onPageChange={handleMovsPageChange}
                adolescente={{
                  id: Number(id),
                  nome_adolescente: formData.nome_adolescente,
                  nome_social: formData.nome_social,
                }}
                showNovaButton
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdolescentesFormPage;
